import { ENVIRONMENT } from '@updater-core/lib/environment';
import { DomainTracker } from '@updater-core/interactors/domain-tracker';
import { mapAddress } from '@updater-apps/full-service-move/utils';
import fetch from '@updater-core/lib/fetch';

const API_ROOT = `${ENVIRONMENT.endpoints.api.url}/v2/relocation`;

const GET_AVAILABLE_APPOINTMENTS_ENDPOINT = `${API_ROOT}/appointments`;
const GET_BOOKED_APPOINTMENTS_ENDPOINT = `${API_ROOT}/appointments/booked`;
const SUBMIT_APPOINTMENT_ENDPOINT = `${API_ROOT}/appointments/[:id]/book`;
const ADDRESS_ENDPOINT = `${API_ROOT}/addresses`;

interface SubmitAddressParams {
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
  requestHeaders: object;
}

interface SubmitAppointmentParams {
  id: string;
  price: number;
  arrivalWindow: string;
  requestHeaders: object;
}

interface GenericSubmit {
  requestHeaders: object;
}

export interface FullServiceMoveGateway {
  getAvailableAppointments: (params: GenericSubmit) => Promise<any>;
  getBookedAppointment: (params: GenericSubmit) => Promise<any>;
  submitFromAddress: (params: SubmitAddressParams) => Promise<any>;
  submitToAddress: (params: SubmitAddressParams) => Promise<any>;
  submitAppointment: (params: SubmitAppointmentParams) => Promise<any>;
}

function handleError(response, tracker, forceError = false) {
  const isError = forceError || response?.errors;

  if (isError) {
    const errorMessage = {
      error:
        response.errors?.[0]?.message ||
        response.errors?.[0]?.messages ||
        'Apologies, we have encountered an error. Please try reloading the page to continue.',
    };
    tracker.actions.trackError('request', errorMessage);
    return errorMessage;
  }
  return undefined;
}

async function makeRequest(
  tracker,
  url,
  requestHeaders = {},
  method = 'GET',
  body = undefined
) {
  const options = {
    cache: 'no-cache',
    method,
    headers: {
      ...requestHeaders,
      'Content-Type': 'application/json',
    },
  };
  if (body) {
    options.body = JSON.stringify(body);
  }

  const fetchResult = await fetch(url, options);
  let fetchData = {};
  try {
    fetchData = await fetchResult?.json();
  } catch (e) {
    tracker.actions.trackError('no_json_result', { url });
  }

  if (fetchResult.status !== 200) {
    return handleError(fetchData, tracker, true);
  }

  const knownAPIErrors = handleError(fetchData, tracker);
  if (knownAPIErrors?.error) {
    return knownAPIErrors;
  }

  return fetchData;
}

function getAvailableAppointments({ tracker }) {
  return async ({ requestHeaders }) => {
    const response = makeRequest(
      tracker,
      GET_AVAILABLE_APPOINTMENTS_ENDPOINT,
      requestHeaders
    );

    return response;
  };
}

function getBookedAppointment({ tracker }) {
  return async ({ requestHeaders }) => {
    const [bookedResponse, savedResponse, availableAppointmentsResponse] =
      await Promise.all([
        makeRequest(tracker, GET_BOOKED_APPOINTMENTS_ENDPOINT, requestHeaders),
        makeRequest(tracker, ADDRESS_ENDPOINT, requestHeaders),
        makeRequest(
          tracker,
          GET_AVAILABLE_APPOINTMENTS_ENDPOINT,
          requestHeaders
        ),
      ]);

    const partialResult = {
      availableAppointments:
        availableAppointmentsResponse && !availableAppointmentsResponse.error
          ? availableAppointmentsResponse
          : [],
    };
    if (bookedResponse && !bookedResponse.error) {
      partialResult.booked = bookedResponse.booked;
      partialResult.fsmAppointmentId = `${bookedResponse.id}`;
    }
    if (savedResponse && !savedResponse.error) {
      if (savedResponse?.from_address)
        partialResult.fromAddress = mapAddress(savedResponse.from_address);
      if (savedResponse?.to_address)
        partialResult.toAddress = mapAddress(savedResponse.to_address);
    }
    return {
      ...partialResult,
    };
  };
}

function submitFromAddress({ tracker }) {
  return async ({ street, unit, city, state, postalCode, requestHeaders }) => {
    const response = makeRequest(
      tracker,
      ADDRESS_ENDPOINT,
      requestHeaders,
      'PUT',
      {
        from_address: {
          street,
          apt: unit,
          city,
          state,
          zip: postalCode,
        },
      }
    );
    return response;
  };
}
function submitToAddress({ tracker }) {
  return async ({ street, unit, city, state, postalCode, requestHeaders }) => {
    const response = makeRequest(
      tracker,
      ADDRESS_ENDPOINT,
      requestHeaders,
      'PUT',
      {
        to_address: {
          street,
          apt: unit,
          city,
          state,
          zip: postalCode,
        },
      }
    );
    return response;
  };
}
function submitAppointment({ tracker }) {
  return async ({ id: appointmentData, price, requestHeaders }) => {
    const { value, arrivalWindow } = JSON.parse(appointmentData);
    const endpoint = SUBMIT_APPOINTMENT_ENDPOINT.split('[:id]').join(value);
    const response = makeRequest(tracker, endpoint, requestHeaders, 'POST', {
      price,
      arrivalWindow,
    });
    return response;
  };
}
export default function createGateway({
  tracker,
}: {
  tracker: DomainTracker;
}): FullServiceMoveGateway {
  return {
    getAvailableAppointments: getAvailableAppointments({ tracker }),
    getBookedAppointment: getBookedAppointment({ tracker }),
    submitFromAddress: submitFromAddress({ tracker }),
    submitToAddress: submitToAddress({ tracker }),
    submitAppointment: submitAppointment({ tracker }),
  };
}
